/* Bosca Bronntanais · Gift Box Builder
 * Vanilla JS. State persists in sessionStorage; steps route via location.hash
 * so browser back/forward work. Items reach the cart as one atomic
 * /cart/add.js call, grouped by a hidden _giftbox_id property.
 * Demo items (non-numeric ids) are stripped before any cart call.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-gift-builder]');
  if (!root) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STORE_KEY = 'glasogGiftBuilder';
  var MSG_MAX = 250;

  var STEP_HASH = { age: 'ce-do', clothing: 'eadai', books: 'leabhair', gifts: 'seoda-beaga', card: 'carta', review: 'reidh' };
  var HASH_STEP = {};
  Object.keys(STEP_HASH).forEach(function (k) { HASH_STEP[STEP_HASH[k]] = k; });

  var AGE_BUCKETS = {
    baby: ['Baby (0-24 months)'],
    preschool: ['Age 2-3', 'Age 3-4'],
    // The kids path covers everything past baby (step 1 is Baby vs Kids).
    kids: ['Age 2-3', 'Age 3-4', 'Age 5-6', 'Age 7-8', 'Age 9-11', 'Age 12-13']
  };
  // In-step refinement on the kids clothing grid: pick the child's age so the
  // grid only offers garments (and sizes) that actually fit.
  var KID_AGE_PILLS = ['Age 2-3', 'Age 3-4', 'Age 5-6', 'Age 7-8', 'Age 9-11', 'Age 12-13'];
  var GARMENT_TYPES = [
    { key: 'T-shirt', ga: 'T-léinte', en: 'T-shirts' },
    { key: 'Sweatshirt', ga: 'Geansaithe', en: 'Sweatshirts' },
    { key: 'Hoodie', ga: 'Húdaithe', en: 'Hoodies' }
  ];
  var BUCKET_RANGES = { 'Age 2-3': [2, 3], 'Age 3-4': [3, 4], 'Age 5-6': [5, 6], 'Age 7-8': [7, 8], 'Age 9-11': [9, 11], 'Age 12-13': [12, 13] };

  // ---------- Payload ----------
  var payloadEl = root.querySelector('[data-giftbox-data]');
  var ITEMS = [];
  try {
    ITEMS = (JSON.parse(payloadEl.textContent).items || []).filter(Boolean);
  } catch (e) {
    console.error('Gift builder: bad payload', e);
    return;
  }
  var BOX_VARIANT = parseInt(root.getAttribute('data-box-variant'), 10);
  var DEMO_MODE = root.getAttribute('data-demo-mode') === 'true';
  var SHIP_THRESHOLD_CENTS = (parseFloat(root.getAttribute('data-shipping-threshold')) || 50) * 100;
  var SHOW_SHIP = root.getAttribute('data-show-shipping') === 'true';
  // When set (e.g. "baby"), the age picker is skipped and every visitor builds
  // for that age. Blank = show the age step. Min items to earn the free box.
  var FORCE_AGE = (root.getAttribute('data-force-age') || '').trim();
  var MIN_ITEMS = 2;              // free box needs 2+ non-card items
  var DISCOUNT_MIN = 3;           // 10% off at 3+ non-card items
  var DISCOUNT_RATE = 0.10;
  var DISCOUNT_CODE = 'BOSCA10';  // Shopify Discounts, min qty 3, applies to all box items incl. cards (verified live 2026-07-13)
  var GIFTS_SUB = {
    baby: 'Soft toys and comforters for little hands.',
    kids: 'Soft toys, notebooks and more to tuck in.',
    adult: 'Stationery and small gifts to tuck in around the edges.'
  };

  // ---------- State ----------
  function freshState() {
    return {
      v: 1,
      giftboxId: 'gb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6),
      age: null,
      kidAge: null,     // kids path: 'Age 5-6' etc., null = all ages
      garb: null,       // kids path: 'T-shirt' | 'Sweatshirt' | 'Hoodie', null = all
      step: 'age',
      selections: {},   // variantId -> {productId, variantId, qty, priceCents, title, sizeLabel, img, cat, demo}
      cardVariantId: null,
      message: ''
    };
  }
  var state = freshState();
  try {
    var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
    if (saved && saved.v === 1) state = saved;
  } catch (e) { /* fresh */ }
  // Sessions persisted before the kids path shipped lack the filter fields.
  if (state.kidAge === undefined) state.kidAge = null;
  if (state.garb === undefined) state.garb = null;

  function persist() {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }

  function baseSteps(age) {
    if (age === 'adult') return ['books', 'gifts', 'card', 'review'];
    return ['clothing', 'books', 'gifts', 'card', 'review'];
  }
  function stepsForAge(age) {
    if (FORCE_AGE) return baseSteps(age);
    return ['age'].concat(baseSteps(age));
  }

  // ---------- Helpers ----------
  var CURRENCY_SYMBOL = (root && root.getAttribute('data-currency-symbol')) || '€';
  function money(cents) { return CURRENCY_SYMBOL + (cents / 100).toFixed(2); }

  function selectionList() {
    return Object.keys(state.selections).map(function (k) { return state.selections[k]; });
  }
  function realSelections() {
    return selectionList().filter(function (s) { return !s.demo && /^\d+$/.test(String(s.variantId)); });
  }
  function realItemUnits() {
    return realSelections().reduce(function (n, s) { return n + s.qty; }, 0);
  }
  // Cards don't count toward the free-box minimum or the 3+ discount.
  function nonCardSelections() {
    return realSelections().filter(function (s) { return s.cat !== 'cards'; });
  }
  function nonCardUnits() {
    return nonCardSelections().reduce(function (n, s) { return n + s.qty; }, 0);
  }
  function nonCardSubtotalCents() {
    return nonCardSelections().reduce(function (sum, s) { return sum + s.priceCents * s.qty; }, 0);
  }
  function discountActive() {
    return nonCardUnits() >= DISCOUNT_MIN;
  }
  function discountCents() {
    if (!discountActive()) return 0;
    // The 10% (BOSCA10) applies to EVERYTHING in the box, card included
    // (verified live 2026-07-13). Shopify rounds the DISCOUNTED line PRICE to
    // the cent and the discount is the remainder — so mirror that (round the
    // net, not the discount). Rounding the discount drifted a cent on lines
    // like €8.95: Math.round(89.5)=90, but Shopify keeps round(805.5)=806 →
    // discount 89, so the basket read €38.21 while the preview said €38.20.
    return realSelections().reduce(function (sum, s) {
      var gross = s.priceCents * s.qty;
      return sum + (gross - Math.round(gross * (1 - DISCOUNT_RATE)));
    }, 0);
  }
  function netTotalCents() {
    return totalCents() - discountCents();
  }
  function updateDiscountUI() {
    var d = discountCents();
    var show = d > 0;
    root.querySelectorAll('[data-gbx-discount]').forEach(function (el) {
      el.style.display = show ? 'flex' : 'none';
      if (show) el.innerHTML = '<span>10% off (3+ items)</span><strong>-' + money(d) + '</strong>';
    });
  }
  function totalCents() {
    return selectionList().reduce(function (sum, s) { return sum + s.priceCents * s.qty; }, 0);
  }
  function itemCount() {
    return selectionList().reduce(function (sum, s) { return sum + s.qty; }, 0);
  }

  // Which buckets does a size label cover? (mirrors glasog-age-filter.mjs)
  function sizeBuckets(label) {
    var s = String(label).toLowerCase();
    // 24-36 months IS the 2-3 years size: keep it on the baby path (buy-to-grow)
    // AND offer it to a 2-3 year old on the kids path.
    if (/\b24-36\b/.test(s)) return ['Baby (0-24 months)', 'Age 2-3'];
    if (/month|\b0-3\b|\b3-6\b|\b6-9\b|\b9-12\b/.test(s)) return ['Baby (0-24 months)'];
    var m = s.match(/age\s*(\d+)\s*-\s*(\d+)/);
    if (!m) {
      var sizeM = s.match(/\(age\s*(\d+)\s*-\s*(\d+)\)/);
      if (sizeM) m = sizeM;
    }
    if (!m) return [];
    var lo = parseInt(m[1], 10), hi = parseInt(m[2], 10);
    var out = [];
    Object.keys(BUCKET_RANGES).forEach(function (b) {
      var r = BUCKET_RANGES[b];
      if (lo <= r[1] && hi >= r[0]) out.push(b);
    });
    return out;
  }

  function intersects(a, b) {
    for (var i = 0; i < a.length; i++) if (b.indexOf(a[i]) !== -1) return true;
    return false;
  }

  function itemVisible(item) {
    if (item.demo && !DEMO_MODE) return false;
    if (state.age === 'adult') {
      if (item.cat === 'cards' || item.cat === 'books') return item.adult || item.cat === 'cards';
      return item.adult === true;
    }
    var buckets = AGE_BUCKETS[state.age] || [];
    // Cards and books are age-agnostic in the box: show them all (a book can go in
    // any box even if pitched at older readers, e.g. Gealach agus Grian in a baby box).
    if (item.cat === 'cards' || item.cat === 'books') return true;
    // Gifts rendered from a path-specific collection (puzzles, notebooks) carry
    // an audience instead of an age_filter metafield.
    // Toys tagged not-under-3 (hard eyes etc.) never show for a 2-3 year old.
    if (item.cat === 'gifts' && item.u3 && state.age === 'kids' && state.kidAge === 'Age 2-3') return false;
    if (item.cat === 'gifts' && item.aud) return item.aud === state.age;
    // Kids clothing refinements: garment type + the child's age.
    if (item.cat === 'clothing' && state.age === 'kids') {
      if (state.garb && item.type !== state.garb) return false;
      if (state.kidAge) {
        // Variant truth beats the metafield: only show it if a size genuinely
        // covering the picked age is in stock.
        var anySized = item.variants.some(function (v) { return sizeBuckets(v.label).length; });
        if (anySized) {
          return item.variants.some(function (v) {
            return v.available && sizeBuckets(v.label).indexOf(state.kidAge) !== -1;
          });
        }
      }
    }
    if (item.ages && item.ages.length) return intersects(item.ages, buckets);
    return false;
  }

  function visibleVariants(item) {
    if (state.age === 'adult') return item.variants;
    var buckets = AGE_BUCKETS[state.age] || [];
    if (state.age === 'kids' && state.kidAge) buckets = [state.kidAge];
    var sized = item.variants.filter(function (v) {
      var vb = sizeBuckets(v.label);
      return vb.length ? intersects(vb, buckets) : false;
    });
    // One-size products (Default Title etc.) have no size buckets: keep all
    var anySized = item.variants.some(function (v) { return sizeBuckets(v.label).length; });
    return anySized ? sized : item.variants;
  }

  // ---------- DOM refs ----------
  var stepEls = {}, railSteps = {};
  root.querySelectorAll('[data-gbx-step]').forEach(function (el) { stepEls[el.getAttribute('data-gbx-step')] = el; });
  root.querySelectorAll('[data-rail-step]').forEach(function (el) { railSteps[el.getAttribute('data-rail-step')] = el; });
  var backBtn = root.querySelector('[data-gbx-back]');
  var nextBtns = root.querySelectorAll('[data-gbx-next]');
  var totalEls = root.querySelectorAll('[data-gbx-total]');
  var bar = root.querySelector('[data-gbx-bar]');
  var barThumb = root.querySelector('[data-gbx-bar-thumb]');
  var barCount = root.querySelector('[data-gbx-bar-count]');
  var boxStage = root.querySelector('[data-gbx-box-stage]');
  var boxItemLists = root.querySelectorAll('[data-gbx-box-items]');
  var sheet = root.querySelector('[data-gbx-sheet]');
  var sheetOverlay = root.querySelector('[data-gbx-sheet-overlay]');
  var flyLayer = root.querySelector('[data-gbx-fly]');
  var msgWrap = root.querySelector('[data-gbx-message-wrap]');
  var msgInput = root.querySelector('[data-gbx-message]');
  var msgCount = root.querySelector('[data-gbx-message-count]');
  var reviewEl = root.querySelector('[data-gbx-review]');
  var errorEl = root.querySelector('[data-gbx-error]');
  var checkoutBtn = root.querySelector('[data-gbx-checkout]');
  var shipBar = root.querySelector('[data-gbx-shipbar]');
  var shipText = root.querySelector('[data-gbx-shiptext]');
  var shipFill = root.querySelector('[data-gbx-shipfill]');

  // ---------- Rendering: product grids ----------
  function cardHtml(item) {
    var vis = visibleVariants(item);
    var oneSize = item.variants.length === 1 && !sizeBuckets(item.variants[0].label).length;
    var html = '<article class="gbx-card' + (item.demo ? ' gbx-card--demo' : '') + (item.cat === 'books' ? ' gbx-card--books' : '') + '" data-gbx-item="' + item.id + '">';
    html += '<div class="gbx-card__imgwrap">';
    html += '<img class="gbx-card__img" src="' + item.img + '" srcset="' + item.img + ' 400w, ' + item.img2x + ' 800w" sizes="(min-width: 768px) 260px, 44vw" alt="' + escapeHtml(item.title) + '" loading="lazy" width="400" height="400">';
    if (item.demo) html += '<span class="gbx-card__ribbon">Sampla · coming soon</span>';
    html += '</div>';
    html += '<h3 class="gbx-card__title">' + escapeHtml(item.title) + '</h3>';
    html += '<p class="gbx-card__price">' + item.priceLabel + '</p>';
    if (oneSize) {
      var v = item.variants[0];
      var inBox = !!state.selections[String(v.id)];
      html += '<button type="button" class="gbx-card__add' + (inBox ? ' is-added' : '') + '"' + (v.available ? '' : ' disabled') + ' aria-pressed="' + (inBox ? 'true' : 'false') + '" data-gbx-add data-variant="' + v.id + '" data-item="' + item.id + '">'
        + (v.available ? (inBox ? '&#10003; Sa bhosca · In the box' : '+ Cuir sa bhosca · Add') : 'Díolta amach · Sold out') + '</button>';
    } else {
      html += '<div class="gbx-card__sizes" role="group" aria-label="Sizes">';
      vis.forEach(function (v) {
        var inBox = !!state.selections[String(v.id)];
        html += '<button type="button" class="gbx-pill' + (inBox ? ' is-added' : '') + '"' + (v.available ? '' : ' disabled')
          + ' aria-pressed="' + (inBox ? 'true' : 'false') + '" aria-label="' + escapeHtml(v.label) + (inBox ? ' — in your box' : '') + '"'
          + ' data-gbx-add data-variant="' + v.id + '" data-item="' + item.id + '" title="' + escapeHtml(v.label) + '">'
          + escapeHtml(shortSize(v.label)) + '</button>';
      });
      html += '</div>';
    }
    html += '</article>';
    return html;
  }

  function cardPickHtml(item) {
    var v = item.variants[0];
    var selected = state.cardVariantId === v.id;
    return '<article class="gbx-card gbx-card--pick' + (selected ? ' is-picked' : '') + '" data-gbx-item="' + item.id + '">'
      + '<button type="button" class="gbx-card__pickbtn" aria-pressed="' + (selected ? 'true' : 'false') + '" data-gbx-pick-card data-variant="' + v.id + '" data-item="' + item.id + '"' + (v.available ? '' : ' disabled') + '>'
      + '<img class="gbx-card__img" src="' + item.img + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="400" height="400">'
      + '<span class="gbx-card__picklabel">' + escapeHtml(item.title.replace(/ Greeting Card$/i, '')) + ' · ' + item.priceLabel + '</span>'
      + '<span class="gbx-card__pickcheck" aria-hidden="true">&#10003;</span>'
      + '</button></article>';
  }

  function shortSize(label) {
    return String(label).replace(/\s*\(\d+\)\s*$/, '').replace(/^Age\s*/i, '').replace(/\s*months/i, 'm');
  }
  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function renderFilters() {
    var wrap = root.querySelector('[data-gbx-filters]');
    if (!wrap) return;
    var show = state.age === 'kids';
    wrap.hidden = !show;
    if (!show) return;
    var pill = function (attr, val, ga, en, active) {
      return '<button type="button" class="gbx-fpill' + (active ? ' is-on' : '') + '" ' + attr + '="' + escapeHtml(val) + '" aria-pressed="' + (active ? 'true' : 'false') + '">'
        + '<span class="gbx-fpill__ga">' + escapeHtml(ga) + '</span>'
        + (en ? '<span class="gbx-fpill__en">' + escapeHtml(en) + '</span>' : '')
        + '</button>';
    };
    var agesEl = wrap.querySelector('[data-gbx-filter-ages]');
    if (agesEl) {
      var ah = pill('data-gbx-filter-age', '', 'Gach aois', 'All ages', !state.kidAge);
      KID_AGE_PILLS.forEach(function (b) {
        ah += pill('data-gbx-filter-age', b, b.replace('Age ', ''), '', state.kidAge === b);
      });
      agesEl.innerHTML = ah;
    }
    var typesEl = wrap.querySelector('[data-gbx-filter-types]');
    if (typesEl) {
      var th = pill('data-gbx-filter-type', '', 'Gach cineál', 'All', !state.garb);
      GARMENT_TYPES.forEach(function (t) {
        th += pill('data-gbx-filter-type', t.key, t.ga, t.en, state.garb === t.key);
      });
      typesEl.innerHTML = th;
    }
  }

  function renderGrids() {
    renderFilters();
    ['clothing', 'books', 'gifts'].forEach(function (cat) {
      var grid = root.querySelector('[data-gbx-grid="' + cat + '"]');
      if (!grid) return;
      var items = ITEMS.filter(function (i) { return i.cat === cat && itemVisible(i); });
      // On the baby path, lead with genuine baby pieces (tagged 'baby') ahead of
      // kids designs that merely offer baby sizes. Stable within each group
      // (preserves collection order). Kids keep pure collection order.
      if (state.age === 'baby') {
        items.sort(function (a, b) { return (b.baby === true ? 1 : 0) - (a.baby === true ? 1 : 0); });
      }
      grid.innerHTML = items.length
        ? items.map(cardHtml).join('')
        : '<p class="gbx-grid__empty">Nothing here for this age just yet. Skip ahead!</p>';
    });
    var cardGrid = root.querySelector('[data-gbx-grid="cards"]');
    if (cardGrid) {
      var cards = ITEMS.filter(function (i) { return i.cat === 'cards' && itemVisible(i); });
      cardGrid.innerHTML = '<article class="gbx-card gbx-card--pick gbx-card--none' + (state.cardVariantId === null ? ' is-picked' : '') + '">'
        + '<button type="button" class="gbx-card__pickbtn" data-gbx-pick-card data-variant="">'
        + '<span class="gbx-card__nonebox" aria-hidden="true"></span>'
        + '<span class="gbx-card__picklabel">Gan chárta · No card</span>'
        + '<span class="gbx-card__pickcheck" aria-hidden="true">&#10003;</span>'
        + '</button></article>'
        + cards.map(cardPickHtml).join('');
    }
    if (msgWrap) msgWrap.hidden = !state.cardVariantId;
  }

  // ---------- The living box ----------
  function fillLevel() {
    var n = itemCount();
    return n === 0 ? 0 : n === 1 ? 1 : n <= 3 ? 2 : 3;
  }

  var displayedTotal = 0;
  function updateBox(skipTicker) {
    // Bar thumbnail: abstract fullness cue (fill-0..3). The big stage keeps the
    // real empty box and shows the customer's ACTUAL items via the overlay.
    var lvl = fillLevel();
    if (barThumb) {
      var active = root.querySelector('.gbx-box__img[data-fill="' + lvl + '"]');
      if (active) barThumb.src = active.src;
    }
    renderRealBox();
    if (barCount) {
      var n = itemCount();
      barCount.hidden = n === 0;
      barCount.textContent = n;
    }
    // items list
    var listHtml = selectionList().map(function (s) {
      return '<li class="gbx-box__item">'
        + '<img src="' + s.img + '" alt="" width="40" height="40" loading="lazy">'
        + '<span class="gbx-box__itemtitle">' + escapeHtml(s.title) + (s.sizeLabel ? ' <em>' + escapeHtml(shortSize(s.sizeLabel)) + '</em>' : '') + (s.demo ? ' <i class="gbx-box__demoflag">sampla</i>' : '') + '</span>'
        + (s.qty > 1 ? '<span class="gbx-box__qty">&times;' + s.qty + '</span>' : '')
        + '<span class="gbx-box__itemprice">' + money(s.priceCents * s.qty) + '</span>'
        + '<button type="button" class="gbx-box__remove" data-gbx-remove="' + s.variantId + '" aria-label="Remove">&times;</button>'
        + '</li>';
    }).join('');
    boxItemLists.forEach(function (ul) {
      ul.innerHTML = listHtml || '<li class="gbx-box__empty">Folamh go fóill · Empty so far</li>';
    });
    // total ticker (net of the 3+ items discount)
    var target = netTotalCents();
    updateDiscountUI();
    if (REDUCED || skipTicker) {
      displayedTotal = target;
      totalEls.forEach(function (el) { el.textContent = money(target); });
    } else {
      tickTotal(target);
    }
    // shipping progress
    if (shipBar && SHOW_SHIP) {
      var t = target;
      shipBar.hidden = t === 0;
      if (t > 0) {
        var pct = Math.min((t / SHIP_THRESHOLD_CENTS) * 100, 100);
        shipFill.style.width = pct + '%';
        shipText.innerHTML = t >= SHIP_THRESHOLD_CENTS
          ? '<strong>Seoladh saor in aisce! · Free shipping unlocked</strong>'
          : money(SHIP_THRESHOLD_CENTS - t) + ' away from free shipping';
      }
    }
    if (checkoutBtn) checkoutBtn.disabled = nonCardUnits() < MIN_ITEMS;
    persist();
  }

  // Layer the customer's actual items into the open box (folded render when
  // available, else the product photo). Max 5 visible; the rest become "+N".
  var realBoxEl = root.querySelector('[data-gbx-box-real]');
  function renderRealBox() {
    if (!realBoxEl) return;
    var sels = selectionList();
    var shown = sels.slice(0, 5);
    var extra = sels.length - shown.length;
    var prevCount = realBoxEl.childElementCount;
    var html = shown.map(function (s, i) {
      var src = (s.foldedImg && FOLDED_OK[s.foldedImg] !== false) ? s.foldedImg : s.img;
      return '<img class="gbx-box__real-item" src="' + src + '" alt="" loading="lazy">';
    }).join('');
    if (extra > 0) html += '<span class="gbx-box__real-more">+' + extra + '</span>';
    realBoxEl.innerHTML = html;
    // pop-in for the newest arrival only
    if (!REDUCED && shown.length > 0 && shown.length + (extra > 0 ? 1 : 0) > prevCount) {
      var last = realBoxEl.children[Math.min(shown.length, realBoxEl.childElementCount) - 1];
      if (last) {
        last.animate(
          [
            { transform: (last.style.transform || '') + ' scale(0.3)', opacity: 0 },
            { transform: (last.style.transform || '') + ' scale(1.08)', opacity: 1, offset: 0.7 },
            { transform: (last.style.transform || '') + ' scale(1)', opacity: 1 }
          ],
          { duration: 340, easing: 'ease-out', delay: 420, fill: 'backwards' }
        );
      }
    }
  }

  var tickRaf = null;
  function tickTotal(target) {
    if (tickRaf) cancelAnimationFrame(tickRaf);
    var from = displayedTotal, start = null, dur = 400;
    function frame(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = Math.round(from + (target - from) * eased);
      totalEls.forEach(function (el) { el.textContent = money(val); });
      if (p < 1) tickRaf = requestAnimationFrame(frame);
      else { displayedTotal = target; tickRaf = null; }
    }
    tickRaf = requestAnimationFrame(frame);
  }

  // ---------- Fly-to-box ----------
  // Foldable items (clothing, totes) fly as a pre-rendered folded shot
  // (item.foldedImg). Availability is cached; a 404 falls back to the photo.
  var FOLDED_OK = {};

  function flyToBox(sourceImg, foldedSrc) {
    if (REDUCED || !sourceImg || !flyLayer) { pulseBox(); return; }
    if (foldedSrc && FOLDED_OK[foldedSrc] === true) { launchFly(sourceImg, foldedSrc); return; }
    if (foldedSrc && FOLDED_OK[foldedSrc] !== false) {
      var started = false;
      var start = function (src) { if (started) return; started = true; launchFly(sourceImg, src); };
      var pre = new Image();
      // Jump the queue ahead of the grid's lazy images
      try { pre.fetchPriority = 'high'; } catch (err) { /* older browsers */ }
      pre.onload = function () { FOLDED_OK[foldedSrc] = true; start(foldedSrc); };
      pre.onerror = function () { FOLDED_OK[foldedSrc] = false; start(null); };
      pre.src = foldedSrc;
      // The fly is decoration (the button already confirmed the add), so give
      // the folded render a decent chance before falling back to the photo.
      setTimeout(function () { start(FOLDED_OK[foldedSrc] === true ? foldedSrc : null); }, 1200);
      return;
    }
    launchFly(sourceImg, null);
  }

  function launchFly(sourceImg, overrideSrc) {
    var target = null;
    if (window.matchMedia('(min-width: 1024px)').matches && boxStage && boxStage.offsetParent) {
      target = boxStage.getBoundingClientRect();
    } else if (barThumb) {
      target = barThumb.getBoundingClientRect();
    }
    if (!target) { pulseBox(); return; }
    var from = sourceImg.getBoundingClientRect();
    var wrap = document.createElement('div');
    var img = document.createElement('img');
    img.src = overrideSrc || sourceImg.currentSrc || sourceImg.src;
    wrap.className = 'gbx-fly__wrap';
    img.className = 'gbx-fly__img' + (overrideSrc ? ' gbx-fly__img--folded' : '');
    wrap.style.left = from.left + 'px';
    wrap.style.top = from.top + 'px';
    wrap.style.width = from.width + 'px';
    wrap.style.height = from.height + 'px';
    wrap.appendChild(img);
    flyLayer.appendChild(wrap);

    var dx = (target.left + target.width / 2) - (from.left + from.width / 2);
    var dy = (target.top + target.height / 2) - (from.top + from.height / 2);
    var scale = Math.max(target.width, 48) / Math.max(from.width, 1) * 0.35;
    var midScale = Math.max(scale, 0.5);

    // Arc: X moves on an ease-out while Y uses an ease-in — the combined path
    // curves. The Y track ends with a small bounce as the item lands.
    var ax = wrap.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + dx + 'px)' }],
      { duration: 760, easing: 'cubic-bezier(.25,.75,.5,1)', fill: 'forwards' }
    );
    img.animate(
      [
        { transform: 'translateY(0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: 'translateY(' + (dy * 0.25 - 60) + 'px) scale(' + midScale + ') rotate(-7deg)', opacity: 1, offset: 0.4 },
        { transform: 'translateY(' + dy + 'px) scale(' + scale + ') rotate(3deg)', opacity: 0.9, offset: 0.76 },
        { transform: 'translateY(' + (dy - 13) + 'px) scale(' + scale + ') rotate(0deg)', opacity: 0.7, offset: 0.88 },
        { transform: 'translateY(' + dy + 'px) scale(' + scale + ') rotate(0deg)', opacity: 0, offset: 1 }
      ],
      { duration: 760, easing: 'cubic-bezier(.5,.05,.75,.5)', fill: 'forwards' }
    );
    ax.onfinish = function () {
      wrap.remove();
      pulseBox();
    };
  }

  // Warm the folded images for the current age's items so the first
  // fly-to-box uses the folded shot without any wait (staggered, idle).
  function preloadFolded() {
    var list = ITEMS.filter(function (i) { return i.foldedImg && FOLDED_OK[i.foldedImg] === undefined && itemVisible(i); });
    var idx = 0;
    (function next() {
      if (idx >= list.length) return;
      var src = list[idx++].foldedImg;
      var img = new Image();
      img.onload = function () { FOLDED_OK[src] = true; setTimeout(next, 60); };
      img.onerror = function () { FOLDED_OK[src] = false; setTimeout(next, 60); };
      img.src = src;
    })();
  }

  // Warm an item's folded render the moment a finger/cursor touches its
  // button, so the fly animation almost never needs the photo fallback.
  root.addEventListener('pointerdown', function (e) {
    var btn = e.target.closest('[data-gbx-add]');
    if (!btn) return;
    var item = findItem(btn.getAttribute('data-item'));
    if (!item || !item.foldedImg || FOLDED_OK[item.foldedImg] !== undefined) return;
    var img = new Image();
    try { img.fetchPriority = 'high'; } catch (err) { /* older browsers */ }
    img.onload = function () { FOLDED_OK[item.foldedImg] = true; };
    img.onerror = function () { FOLDED_OK[item.foldedImg] = false; };
    img.src = item.foldedImg;
  }, { passive: true });

  function pulseBox() {
    var targets = [];
    if (boxStage && boxStage.offsetParent) targets.push(boxStage);
    if (barThumb && barThumb.offsetParent) targets.push(barThumb);
    targets.forEach(function (el) {
      if (REDUCED) return;
      el.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.12, 0.9)' },
          { transform: 'scale(0.94, 1.08)' },
          { transform: 'scale(1)' }
        ],
        { duration: 420, easing: 'ease-out' }
      );
    });
  }

  // ---------- Steps ----------
  function currentSteps() { return stepsForAge(state.age); }

  // Order #1234: .gbx-rail is position:sticky, so once it is stuck its
  // offsetTop tracks the CURRENT scroll position instead of the rail's place
  // in the document. The old scrollTo({ top: rail.offsetTop, behavior:
  // 'smooth' }) therefore meant "stay where you are" whenever Next was tapped
  // from the bottom of a step on mobile, and the customer landed mid-way down
  // the next step (she never saw the card grid, only the message box).
  // Measure the target from .gbx-body instead (static, so its document-space
  // position is honest), and jump instantly on the frame after the new step
  // renders: mobile Chrome cancels in-flight smooth scrolls when the step
  // swap reflows the page. The instant jump also satisfies
  // prefers-reduced-motion (nothing animates).
  function scrollToStepTop() {
    var jump = function () {
      var body = root.querySelector('.gbx-body') || root;
      var rail = root.querySelector('.gbx-rail');
      var railH = rail ? rail.offsetHeight : 0;
      var y = body.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - railH;
      window.scrollTo(0, Math.max(0, y));
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(jump); else jump();
  }

  function showStep(step, viaHash) {
    var steps = currentSteps();
    if (steps.indexOf(step) === -1) step = steps[0];
    if (steps.indexOf('age') !== -1 && !state.age) step = 'age';
    state.step = step;
    var giftsSub = root.querySelector('[data-gbx-gifts-sub]');
    if (giftsSub) giftsSub.textContent = GIFTS_SUB[state.age] || GIFTS_SUB.baby;
    Object.keys(stepEls).forEach(function (k) { stepEls[k].hidden = k !== step; });
    // rail
    var reached = steps.indexOf(step);
    Object.keys(railSteps).forEach(function (k) {
      var el = railSteps[k];
      var idx = steps.indexOf(k);
      el.classList.toggle('is-hidden', idx === -1);
      el.classList.toggle('is-active', k === step);
      el.classList.toggle('is-done', idx !== -1 && idx < reached);
      el.classList.toggle('is-clickable', idx !== -1 && idx <= reached);
    });
    // nav buttons
    var idx = steps.indexOf(step);
    if (backBtn) backBtn.hidden = idx <= 0;
    nextBtns.forEach(function (b) { b.hidden = step === 'age' || step === 'review'; });
    if (bar) bar.classList.toggle('is-visible', step !== 'age');
    if (step === 'review') renderReview();
    if (!viaHash) {
      var h = '#' + STEP_HASH[step];
      if (location.hash !== h) {
        try { history.pushState(null, '', h); } catch (e) { location.hash = h; }
      }
    }
    scrollToStepTop();
    persist();
  }

  function move(delta) {
    var steps = currentSteps();
    var idx = steps.indexOf(state.step) + delta;
    if (idx >= 0 && idx < steps.length) showStep(steps[idx]);
  }

  // ---------- Review ----------
  function renderReview() {
    if (!reviewEl) return;
    var sels = selectionList();
    var demos = sels.filter(function (s) { return s.demo; });
    var hasCard = sels.some(function (s) { return s.cat === 'cards'; });
    var html = '<ul class="gbx-review__list">';
    html += '<li class="gbx-review__item gbx-review__item--box">'
      + '<img src="' + (root.querySelector('.gbx-box__img[data-fill="0"]') || {}).src + '" alt="" width="48" height="48">'
      + '<span class="gbx-box__itemtitle">Glasóg Woodland Gift Box' + (state.message && hasCard ? ' + handwritten card message' : '') + '</span>'
      + '<span class="gbx-review__free">Saor in aisce · FREE</span></li>';
    sels.forEach(function (s) {
      html += '<li class="gbx-review__item">'
        + '<img src="' + s.img + '" alt="" width="48" height="48">'
        + '<span class="gbx-box__itemtitle">' + escapeHtml(s.title) + (s.sizeLabel ? ' <em>' + escapeHtml(shortSize(s.sizeLabel)) + '</em>' : '') + (s.demo ? ' <i class="gbx-box__demoflag">sampla</i>' : '') + '</span>'
        + (s.qty > 1 ? '<span class="gbx-box__qty">&times;' + s.qty + '</span>' : '')
        + '<span class="gbx-box__itemprice">' + money(s.priceCents * s.qty) + '</span>'
        + '<button type="button" class="gbx-box__remove" data-gbx-remove="' + s.variantId + '" aria-label="Remove">&times;</button></li>';
    });
    html += '</ul>';
    if (!hasCard) {
      // Order #1234: a customer who scrolled past the card grid assumed a
      // card was included. Say plainly that there is no card, with a way back
      // to the card step before checkout.
      html += '<div class="gbx-review__nocard" data-gbx-nocard style="margin-top:0.75rem;padding:0.8rem 1rem;background:#F3EEE4;border:1px solid #D8CBB4;border-radius:10px;color:#4F5D4E;font-size:0.95rem;line-height:1.45;">'
        + '<strong>No card in this box.</strong> Your box will arrive without a greeting card or message. '
        + '<button type="button" class="gbx-review__addcard" data-gbx-goto-card style="background:none;border:none;padding:0;font:inherit;color:#4F5D4E;font-weight:700;text-decoration:underline;cursor:pointer;">Cuir c&aacute;rta leis &middot; Add a card</button>'
        + '</div>';
    }
    if (state.message && hasCard) {
      html += '<div class="gbx-review__msg"><span>Teachtaireacht · Message</span><p>&ldquo;' + escapeHtml(state.message) + '&rdquo;</p></div>';
    } else if (state.message) {
      html += '<div class="gbx-review__msg" style="border:1px solid #D8CBB4;background:#F3EEE4;"><span>Teachtaireacht · Message</span><p>&ldquo;' + escapeHtml(state.message) + '&rdquo;</p><p style="margin-top:0.4rem;color:#8a5a2b;font-size:0.9rem;">Your message needs a card to be handwritten inside · add one in the Card &amp; Message step.</p></div>';
    }
    if (discountActive() && discountCents() > 0) {
      html += '<div class="gbx-review__discount" style="display:flex;justify-content:space-between;color:#4F5D4E;font-weight:600;font-size:0.95rem;margin-top:0.5rem;"><span>10% off (3 or more items)</span><strong>-' + money(discountCents()) + '</strong></div>';
    }
    html += '<div class="gbx-review__total"><span>Iomlán · Total</span><strong>' + money(netTotalCents()) + '</strong></div>';
    if (nonCardUnits() < MIN_ITEMS) {
      html += '<p class="gbx-review__min-note" style="margin-top:1rem;padding:0.8rem 1rem;background:#F3EEE4;border:1px solid #D8CBB4;border-radius:10px;color:#4F5D4E;font-size:0.95rem;line-height:1.45;">Add at least <strong>2 items</strong> (a card doesn\'t count) to your box. Your Glasóg woodland gift box is included <strong>free</strong> when your box holds two or more items.</p>';
    } else if (nonCardUnits() < DISCOUNT_MIN) {
      html += '<p class="gbx-review__tip" style="margin-top:0.75rem;color:#7A8577;font-size:0.9rem;">Add ' + (DISCOUNT_MIN - nonCardUnits()) + ' more and save 10% on your items.</p>';
    }
    if (demos.length) {
      html += '<p class="gbx-review__demo-note">' + demos.length + ' sample item(s) shown for preview only; they won’t be added to your basket.</p>';
    }
    reviewEl.innerHTML = html;
  }

  // ---------- Selection ----------
  function findItem(id) {
    for (var i = 0; i < ITEMS.length; i++) if (String(ITEMS[i].id) === String(id)) return ITEMS[i];
    return null;
  }
  function findVariant(item, variantId) {
    for (var i = 0; i < item.variants.length; i++) if (String(item.variants[i].id) === String(variantId)) return item.variants[i];
    return null;
  }

  function toggleSelection(itemId, variantId, sourceImg) {
    var key = String(variantId);
    if (state.selections[key]) {
      delete state.selections[key];
    } else {
      var item = findItem(itemId);
      if (!item) return;
      var v = findVariant(item, variantId);
      if (!v || !v.available) return;
      var sizeLabel = v.label === 'Default Title' ? '' : v.label;
      state.selections[key] = {
        productId: item.id, variantId: v.id, qty: 1,
        priceCents: v.priceCents, title: item.title, sizeLabel: sizeLabel,
        img: item.img, foldedImg: item.foldedImg || null, cat: item.cat, demo: !!item.demo
      };
      flyToBox(sourceImg, item.foldedImg || null);
    }
    renderGrids();
    updateBox();
  }

  function pickCard(variantId, itemId) {
    // deselect old card selection
    if (state.cardVariantId) delete state.selections[String(state.cardVariantId)];
    if (!variantId) {
      state.cardVariantId = null;
      state.message = '';
      if (msgInput) msgInput.value = '';
    } else {
      var item = findItem(itemId);
      var v = item && findVariant(item, variantId);
      if (!v || !v.available) return;
      state.cardVariantId = v.id;
      state.selections[String(v.id)] = {
        productId: item.id, variantId: v.id, qty: 1,
        priceCents: v.priceCents, title: item.title, sizeLabel: '',
        img: item.img, foldedImg: null, cat: 'cards', demo: !!item.demo
      };
    }
    renderGrids();
    updateBox();
  }

  // ---------- Checkout ----------
  function showError(msg) {
    if (!errorEl) return;
    errorEl.hidden = false;
    errorEl.textContent = msg;
  }

  function addToCart() {
    var real = realSelections();
    if (!real.length) {
      showError('Your box only has sample items in it. Add at least one real item to order.');
      return;
    }
    if (nonCardUnits() < MIN_ITEMS) {
      showError('Please add at least 2 items (a card doesn\'t count) to your box. The free Glasóg gift box needs two or more items.');
      return;
    }
    // The handwritten message travels only inside a card. Derive the card from
    // the selections themselves (never a possibly-stale cardVariantId) so the
    // message can't be silently dropped between review and basket.
    var cardSel = null;
    real.forEach(function (s) { if (s.cat === 'cards') cardSel = s; });
    if (state.message.trim() && !cardSel) {
      showError('Your gift message needs a card to be handwritten inside. Add a card in the Card & Message step, or clear the message.');
      return;
    }
    if (errorEl) errorEl.hidden = true;
    checkoutBtn.disabled = true;
    checkoutBtn.classList.add('is-busy');
    checkoutBtn.textContent = 'Ag pacáil… Packing your box';

    var items = [{
      id: BOX_VARIANT, quantity: 1,
      properties: { _giftbox_id: state.giftboxId, _giftbox_role: 'box' }
    }];
    real.forEach(function (s) {
      var props = { _giftbox_id: state.giftboxId };
      if (cardSel && String(s.variantId) === String(cardSel.variantId) && state.message.trim()) {
        props['Gift Message'] = state.message.trim().slice(0, MSG_MAX);
      }
      items.push({ id: parseInt(s.variantId, 10), quantity: s.qty, properties: props });
    });

    function attempt(retriesLeft) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (res.status === 429 && retriesLeft > 0) {
          setTimeout(function () { attempt(retriesLeft - 1); }, 1500);
          return null;
        }
        if (!res.ok) {
          return res.json().then(function (err) { throw new Error(err.description || err.message || 'Cart error'); });
        }
        return res.json();
      }).then(function (data) {
        if (!data) return; // retry scheduled
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Gift order': 'yes' } })
        }).then(function () {
          try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
          window.location.href = discountActive()
            ? '/discount/' + DISCOUNT_CODE + '?redirect=/cart'
            : '/cart';
        });
      }).catch(function (err) {
        checkoutBtn.disabled = false;
        checkoutBtn.classList.remove('is-busy');
        checkoutBtn.textContent = 'Cuir sa chiseán · Add box to basket';
        showError('We couldn’t add everything: ' + err.message + ' Remove that item and try again.');
      });
    }
    attempt(1);
  }

  // ---------- Events ----------
  root.addEventListener('click', function (e) {
    var ageBtn = e.target.closest('[data-gbx-age]');
    if (ageBtn) {
      state.age = ageBtn.getAttribute('data-gbx-age');
      state.kidAge = null;
      state.garb = null;
      renderGrids();
      updateBox();
      showStep(currentSteps()[1]);
      if (!REDUCED) setTimeout(preloadFolded, 250);
      return;
    }
    var fAge = e.target.closest('[data-gbx-filter-age]');
    if (fAge) {
      state.kidAge = fAge.getAttribute('data-gbx-filter-age') || null;
      renderGrids();
      persist();
      return;
    }
    var fType = e.target.closest('[data-gbx-filter-type]');
    if (fType) {
      state.garb = fType.getAttribute('data-gbx-filter-type') || null;
      renderGrids();
      persist();
      return;
    }
    var addBtn = e.target.closest('[data-gbx-add]');
    if (addBtn && !addBtn.disabled) {
      var card = addBtn.closest('.gbx-card');
      var img = card ? card.querySelector('.gbx-card__img') : null;
      toggleSelection(addBtn.getAttribute('data-item'), addBtn.getAttribute('data-variant'), img);
      return;
    }
    var pickBtn = e.target.closest('[data-gbx-pick-card]');
    if (pickBtn && !pickBtn.disabled) {
      pickCard(pickBtn.getAttribute('data-variant'), pickBtn.getAttribute('data-item'));
      return;
    }
    var removeBtn = e.target.closest('[data-gbx-remove]');
    if (removeBtn) {
      var vid = removeBtn.getAttribute('data-gbx-remove');
      if (String(state.cardVariantId) === String(vid)) {
        // The message lives inside the card — removing the card removes it too.
        state.cardVariantId = null;
        state.message = '';
        if (msgInput) msgInput.value = '';
        if (msgCount) msgCount.textContent = '0';
      }
      delete state.selections[String(vid)];
      renderGrids();
      updateBox();
      if (state.step === 'review') renderReview();
      return;
    }
    if (e.target.closest('[data-gbx-goto-card]')) { showStep('card'); return; }
    if (e.target.closest('[data-gbx-next]')) { move(1); return; }
    if (e.target.closest('[data-gbx-back]')) { move(-1); return; }
    if (e.target.closest('[data-gbx-checkout]')) { addToCart(); return; }
    if (e.target.closest('[data-gbx-bar-open]')) { openSheet(true); return; }
    var railStep = e.target.closest('[data-rail-step]');
    if (railStep && railStep.classList.contains('is-clickable')) {
      showStep(railStep.getAttribute('data-rail-step'));
    }
  });

  if (sheetOverlay) sheetOverlay.addEventListener('click', function () { openSheet(false); });

  function openSheet(open) {
    if (!sheet) return;
    sheet.classList.toggle('is-open', open);
    sheetOverlay.classList.toggle('is-open', open);
    sheet.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  if (msgInput) {
    msgInput.addEventListener('input', function () {
      state.message = msgInput.value.slice(0, MSG_MAX);
      if (msgCount) msgCount.textContent = state.message.length;
      persist();
    });
  }

  window.addEventListener('hashchange', function () {
    var step = HASH_STEP[location.hash.replace('#', '')];
    if (step) showStep(step, true);
  });

  // Shopify's unpublished-theme PREVIEW bar docks to the bottom on mobile and
  // covers our sticky bar (it doesn't exist on the published theme). Detect it
  // and lift the builder bar + sheet above it. It injects late, so re-check.
  function offsetForPreviewBar() {
    // The preview iframe is a full-viewport transparent overlay whose tap-eating
    // pill sits in the bottom ~80px; its inside can't be measured (cross-origin),
    // so lift our bar by a fixed amount whenever the iframe exists.
    var pb = document.getElementById('PBarNextFrame') || document.getElementById('preview-bar-iframe');
    var lift = pb ? 88 : 0;
    if (bar) bar.style.bottom = lift ? lift + 'px' : '';
    if (sheet) sheet.style.bottom = lift ? lift + 'px' : '';
  }
  [800, 2000, 4500].forEach(function (ms) { setTimeout(offsetForPreviewBar, ms); });
  window.addEventListener('resize', offsetForPreviewBar, { passive: true });

  // ---------- Init ----------
  if (FORCE_AGE) {
    state.age = FORCE_AGE;
    if (state.step === 'age') state.step = null;
  }
  renderGrids();
  updateBox(true);
  if (msgInput && state.message) { msgInput.value = state.message; if (msgCount) msgCount.textContent = state.message.length; }
  var initialStep = HASH_STEP[location.hash.replace('#', '')] || state.step || currentSteps()[0];
  showStep(initialStep, true);
  if (state.age && !REDUCED) setTimeout(preloadFolded, 250);
})();
